import React, { useState, useEffect } from 'react';
import {
  Save,
  Plus,
  Trash2,
  Pencil,
  X,
  Check,
  Building,
  CreditCard,
  DollarSign,
  Smartphone,
  Globe,
  FileText,
  AlertCircle,
  CheckCircle2,
  Tag,
  RotateCcw,
  FolderPlus
} from 'lucide-react';
import { financeApi } from '../../../../lib/api/finance';
import type {
  CompanyFinanceSettings,
  Currency,
  BankAccount,
  MobileMoneyAccount,
  FinanceCategoryOption
} from '../../../../lib/types/finance';
import {
  DEFAULT_SUPPLIER_CATEGORIES,
  DEFAULT_INVOICE_CATEGORIES,
  normalizeCategories,
  slugifyCategory
} from '../utils/categoryUtils';
import { useConfirm } from '../../../../components/ui/ConfirmProvider';

export const FinanceSettingsPage: React.FC = () => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Settings state
  const [settings, setSettings] = useState<CompanyFinanceSettings | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  // Company Profile fields
  const [companyName, setCompanyName] = useState<string>('');
  const [legalName, setLegalName] = useState<string>('');
  const [tagline, setTagline] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [whatsapp, setWhatsapp] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [website, setWebsite] = useState<string>('');
  const [tin, setTin] = useState<string>('');
  const [regNo, setRegNo] = useState<string>('');
  const [vat, setVat] = useState<string>('');

  // Bank Accounts
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // Mobile Money Accounts
  const [momoAccounts, setMomoAccounts] = useState<MobileMoneyAccount[]>([]);

  // Categories state
  const [invoiceCategories, setInvoiceCategories] = useState<FinanceCategoryOption[]>(DEFAULT_INVOICE_CATEGORIES);
  const [supplierCategories, setSupplierCategories] = useState<FinanceCategoryOption[]>(DEFAULT_SUPPLIER_CATEGORIES);
  const [newInvoiceCategoryLabel, setNewInvoiceCategoryLabel] = useState<string>('');
  const [newSupplierCategoryLabel, setNewSupplierCategoryLabel] = useState<string>('');
  const [editingInvoiceCatId, setEditingInvoiceCatId] = useState<string | null>(null);
  const [editingInvoiceCatLabel, setEditingInvoiceCatLabel] = useState<string>('');
  const [editingSupplierCatId, setEditingSupplierCatId] = useState<string | null>(null);
  const [editingSupplierCatLabel, setEditingSupplierCatLabel] = useState<string>('');

  // Default Templates
  const [invoiceNotes, setInvoiceNotes] = useState<string>('');
  const [invoiceTerms, setInvoiceTerms] = useState<string>('');
  const [receiptNotice, setReceiptNotice] = useState<string>('');
  const [voucherSupplierInst, setVoucherSupplierInst] = useState<string>('');
  const [voucherClientInst, setVoucherClientInst] = useState<string>('');
  const [voucherTerms, setVoucherTerms] = useState<string>('');

  // New Currency modal / inputs
  const [showAddCurrency, setShowAddCurrency] = useState<boolean>(false);
  const [newCurrCode, setNewCurrCode] = useState<string>('');
  const [newCurrName, setNewCurrName] = useState<string>('');
  const [newCurrSymbol, setNewCurrSymbol] = useState<string>('');
  const [newCurrRate, setNewCurrRate] = useState<number>(1.0);

  // Edit currency inline state
  const [editingCurrId, setEditingCurrId] = useState<number | null>(null);
  const [editCurrName, setEditCurrName] = useState<string>('');
  const [editCurrSymbol, setEditCurrSymbol] = useState<string>('');
  const [editCurrRate, setEditCurrRate] = useState<number>(1.0);
  const [editCurrIsBase, setEditCurrIsBase] = useState<boolean>(false);
  const [editCurrIsActive, setEditCurrIsActive] = useState<boolean>(true);
  const [savingCurrId, setSavingCurrId] = useState<number | null>(null);
  const [deletingCurrId, setDeletingCurrId] = useState<number | null>(null);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [s, cList] = await Promise.all([
        financeApi.getSettings(),
        financeApi.getCurrencies(false)
      ]);

      setSettings(s);
      setCurrencies(cList);

      setCompanyName(s.company_name || '');
      setLegalName(s.legal_company_name || '');
      setTagline(s.tagline || '');
      setAddress(s.physical_address || '');
      setPhone(s.phone || '');
      setWhatsapp(s.whatsapp || '');
      setEmail(s.email || '');
      setWebsite(s.website || '');
      setTin(s.tin_number || '');
      setRegNo(s.company_registration_number || '');
      setVat(s.vat_number || '');

      setBankAccounts(s.bank_accounts || []);
      setMomoAccounts(s.mobile_money_accounts || []);

      setInvoiceCategories(normalizeCategories(s.invoice_categories, DEFAULT_INVOICE_CATEGORIES));
      setSupplierCategories(normalizeCategories(s.supplier_categories, DEFAULT_SUPPLIER_CATEGORIES));

      setInvoiceNotes(s.default_invoice_notes || '');
      setInvoiceTerms(s.default_invoice_terms || '');
      setReceiptNotice(s.default_receipt_notice || '');
      setVoucherSupplierInst(s.default_voucher_supplier_instructions || '');
      setVoucherClientInst(s.default_voucher_client_instructions || '');
      setVoucherTerms(s.default_voucher_terms || '');
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await financeApi.updateSettings({
        company_name: companyName,
        legal_company_name: legalName,
        tagline,
        physical_address: address,
        phone,
        whatsapp,
        email,
        website,
        tin_number: tin,
        company_registration_number: regNo,
        vat_number: vat,
        bank_accounts: bankAccounts,
        mobile_money_accounts: momoAccounts,
        invoice_categories: invoiceCategories,
        supplier_categories: supplierCategories,
        default_invoice_notes: invoiceNotes,
        default_invoice_terms: invoiceTerms,
        default_receipt_notice: receiptNotice,
        default_voucher_supplier_instructions: voucherSupplierInst,
        default_voucher_client_instructions: voucherClientInst,
        default_voucher_terms: voucherTerms
      });
      setMessage({ type: 'success', text: 'Finance and company settings saved successfully!' });
      setTimeout(() => setMessage(null), 4000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  // Add Bank Account
  const addBankAccount = () => {
    setBankAccounts([
      ...bankAccounts,
      {
        bank_name: '',
        account_name: legalName || 'Allbound Travel Services Limited',
        account_number: '',
        branch: '',
        swift_bic: '',
        currency: 'USD',
        is_primary: bankAccounts.length === 0
      }
    ]);
  };

  // Add Mobile Money
  const addMomoAccount = () => {
    setMomoAccounts([
      ...momoAccounts,
      {
        network: 'MTN Mobile Money',
        merchant_number: '',
        account_name: legalName || 'Allbound Travel Services Limited',
        currency: 'UGX',
        is_primary: momoAccounts.length === 0
      }
    ]);
  };

  // Add Currency
  const handleCreateCurrency = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCurrCode.trim() || !newCurrName.trim() || !newCurrSymbol.trim()) return;

    try {
      await financeApi.createCurrency({
        code: newCurrCode.trim().toUpperCase(),
        name: newCurrName.trim(),
        symbol: newCurrSymbol.trim(),
        exchange_rate_to_usd: Number(newCurrRate) || 1.0,
        is_base_currency: false,
        is_active: true
      });
      setShowAddCurrency(false);
      setNewCurrCode('');
      setNewCurrName('');
      setNewCurrSymbol('');
      setNewCurrRate(1.0);
      const cList = await financeApi.getCurrencies(false);
      setCurrencies(cList);
      setMessage({ type: 'success', text: 'Currency added successfully!' });
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to create currency');
    }
  };

  const handleUpdateCurrencyRate = async (curr: Currency, newRate: number) => {
    try {
      await financeApi.updateCurrency(curr.id, { exchange_rate_to_usd: newRate });
      const cList = await financeApi.getCurrencies(false);
      setCurrencies(cList);
    } catch (err: any) {
      alert('Failed to update rate');
    }
  };

  const startEditCurrency = (curr: Currency) => {
    setEditingCurrId(curr.id);
    setEditCurrName(curr.name);
    setEditCurrSymbol(curr.symbol);
    setEditCurrRate(curr.exchange_rate_to_usd);
    setEditCurrIsBase(curr.is_base_currency);
    setEditCurrIsActive(curr.is_active);
  };

  const cancelEditCurrency = () => setEditingCurrId(null);

  const handleSaveCurrencyEdit = async (currId: number) => {
    setSavingCurrId(currId);
    try {
      await financeApi.updateCurrency(currId, {
        name: editCurrName,
        symbol: editCurrSymbol,
        exchange_rate_to_usd: editCurrRate,
        is_base_currency: editCurrIsBase,
        is_active: editCurrIsActive
      });
      const cList = await financeApi.getCurrencies(false);
      setCurrencies(cList);
      setEditingCurrId(null);
      setMessage({ type: 'success', text: 'Currency updated successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to update currency');
    } finally {
      setSavingCurrId(null);
    }
  };

  const handleDeleteCurrency = async (curr: Currency) => {
    if (curr.is_base_currency) {
      alert('Cannot delete the base currency. Set another currency as base first.');
      return;
    }
    const confirmed = await confirm({
      title: 'Delete Currency',
      message: `Delete ${curr.code} (${curr.name})? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    setDeletingCurrId(curr.id);
    try {
      await financeApi.deleteCurrency(curr.id);
      const cList = await financeApi.getCurrencies(false);
      setCurrencies(cList);
      setMessage({ type: 'success', text: `${curr.code} removed.` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete currency');
    } finally {
      setDeletingCurrId(null);
    }
  };

  // Category management handlers
  const handleAddInvoiceCategory = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newInvoiceCategoryLabel.trim();
    if (!trimmed) return;
    const newId = slugifyCategory(trimmed);
    if (invoiceCategories.some((c) => c.id === newId || c.label.toLowerCase() === trimmed.toLowerCase())) {
      setMessage({ type: 'error', text: `Invoice category "${trimmed}" already exists.` });
      return;
    }
    setInvoiceCategories([...invoiceCategories, { id: newId, label: trimmed, is_default: false }]);
    setNewInvoiceCategoryLabel('');
    setMessage({ type: 'success', text: `Added "${trimmed}" to invoice categories. Remember to click "Save Settings".` });
  };

  const handleAddSupplierCategory = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newSupplierCategoryLabel.trim();
    if (!trimmed) return;
    const newId = slugifyCategory(trimmed);
    if (supplierCategories.some((c) => c.id === newId || c.label.toLowerCase() === trimmed.toLowerCase())) {
      setMessage({ type: 'error', text: `Supplier category "${trimmed}" already exists.` });
      return;
    }
    setSupplierCategories([...supplierCategories, { id: newId, label: trimmed, is_default: false }]);
    setNewSupplierCategoryLabel('');
    setMessage({ type: 'success', text: `Added "${trimmed}" to supplier categories. Remember to click "Save Settings".` });
  };

  const handleSaveEditInvoiceCategory = (id: string) => {
    const trimmed = editingInvoiceCatLabel.trim();
    if (!trimmed) return;
    setInvoiceCategories((prev) => prev.map((c) => (c.id === id ? { ...c, label: trimmed } : c)));
    setEditingInvoiceCatId(null);
    setEditingInvoiceCatLabel('');
  };

  const handleSaveEditSupplierCategory = (id: string) => {
    const trimmed = editingSupplierCatLabel.trim();
    if (!trimmed) return;
    setSupplierCategories((prev) => prev.map((c) => (c.id === id ? { ...c, label: trimmed } : c)));
    setEditingSupplierCatId(null);
    setEditingSupplierCatLabel('');
  };

  const handleDeleteInvoiceCategory = async (id: string) => {
    if (invoiceCategories.length <= 1) {
      setMessage({ type: 'error', text: 'At least one invoice category must be kept.' });
      return;
    }
    const confirmed = await confirm({
      title: 'Delete Category',
      message: 'Are you sure you want to remove this invoice category?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      setInvoiceCategories((prev) => prev.filter((c) => c.id !== id));
      setMessage({ type: 'success', text: 'Category removed from list. Remember to click "Save Settings".' });
    }
  };

  const handleDeleteSupplierCategory = async (id: string) => {
    if (supplierCategories.length <= 1) {
      setMessage({ type: 'error', text: 'At least one supplier category must be kept.' });
      return;
    }
    const confirmed = await confirm({
      title: 'Delete Category',
      message: 'Are you sure you want to remove this supplier category?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      setSupplierCategories((prev) => prev.filter((c) => c.id !== id));
      setMessage({ type: 'success', text: 'Category removed from list. Remember to click "Save Settings".' });
    }
  };

  const handleResetInvoiceCategories = async () => {
    const confirmed = await confirm({
      title: 'Reset Invoice Categories',
      message: 'Reset invoice categories back to system defaults?',
      confirmText: 'Reset',
      variant: 'danger',
    });
    if (confirmed) {
      setInvoiceCategories([...DEFAULT_INVOICE_CATEGORIES]);
      setMessage({ type: 'success', text: 'Invoice categories reset to defaults.' });
    }
  };

  const handleResetSupplierCategories = async () => {
    const confirmed = await confirm({
      title: 'Reset Supplier Categories',
      message: 'Reset supplier categories back to system defaults?',
      confirmText: 'Reset',
      variant: 'danger',
    });
    if (confirmed) {
      setSupplierCategories([...DEFAULT_SUPPLIER_CATEGORIES]);
      setMessage({ type: 'success', text: 'Supplier categories reset to defaults.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto my-6 px-4 space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-playfair text-gray-900">
            Finance Settings & Currencies
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure company legal info, wire instructions, mobile money numbers, and exchange rates
          </p>
        </div>

        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-800 hover:bg-teal-900 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-150 disabled:opacity-50 cursor-pointer"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl text-sm flex items-center ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2 text-red-600 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}

      {/* SECTION 1: CURRENCY MANAGEMENT WITH EXCHANGE RATES */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-teal-900 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-teal-700" /> Multi-Currency & USD Exchange Rates
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Add currencies and define how many units convert to 1 USD. Invoices and receipts can be issued in any active currency.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddCurrency(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-50 hover:bg-teal-100 text-teal-900 border border-teal-200 text-xs font-semibold rounded-lg shadow-2xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Add New Currency
          </button>
        </div>

        {/* Currency Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 text-gray-600 font-bold uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Currency Name</th>
                <th className="px-4 py-3 text-center">Symbol</th>
                <th className="px-4 py-3 text-left">Units per 1 USD</th>
                <th className="px-4 py-3 text-center">Base</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {currencies.map((curr) => (
                <React.Fragment key={curr.id}>
                  <tr className={`hover:bg-gray-50 ${editingCurrId === curr.id ? 'bg-teal-50/40' : ''}`}>
                    <td className="px-4 py-3 font-mono font-bold text-teal-900 text-sm">{curr.code}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{curr.name}</td>
                    <td className="px-4 py-3 text-center font-bold text-gray-700">{curr.symbol}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">
                      {curr.is_base_currency
                        ? <span className="text-gray-400">1.0000 (Base)</span>
                        : `${curr.exchange_rate_to_usd.toFixed(4)}`
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      {curr.is_base_currency
                        ? <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800">Base</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                        curr.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {curr.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          title="Edit currency"
                          onClick={() => editingCurrId === curr.id ? cancelEditCurrency() : startEditCurrency(curr)}
                          className="p-1.5 rounded-lg text-teal-700 hover:bg-teal-100 transition cursor-pointer"
                        >
                          {editingCurrId === curr.id ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          title={curr.is_base_currency ? 'Cannot delete base currency' : 'Delete currency'}
                          onClick={() => handleDeleteCurrency(curr)}
                          disabled={deletingCurrId === curr.id || curr.is_base_currency}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Inline Edit Row */}
                  {editingCurrId === curr.id && (
                    <tr className="bg-teal-50/60 border-t border-teal-200">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 text-xs items-end">
                          <div className="sm:col-span-2">
                            <label className="block text-gray-600 font-semibold mb-1">Currency Name *</label>
                            <input
                              type="text"
                              value={editCurrName}
                              onChange={(e) => setEditCurrName(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-gray-600 font-semibold mb-1">Symbol *</label>
                            <input
                              type="text"
                              value={editCurrSymbol}
                              onChange={(e) => setEditCurrSymbol(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-bold"
                            />
                          </div>
                          <div>
                            <label className="block text-gray-600 font-semibold mb-1">Rate to 1 USD</label>
                            <input
                              type="number"
                              step="0.0001"
                              min="0.0001"
                              value={editCurrRate}
                              disabled={editCurrIsBase}
                              onChange={(e) => setEditCurrRate(parseFloat(e.target.value) || 1.0)}
                              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono disabled:bg-gray-100 disabled:text-gray-400"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editCurrIsBase}
                                onChange={(e) => {
                                  setEditCurrIsBase(e.target.checked);
                                  if (e.target.checked) setEditCurrRate(1.0);
                                }}
                                className="w-4 h-4 accent-teal-700"
                              />
                              <span className="text-gray-700 font-semibold">Set as Base</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editCurrIsActive}
                                onChange={(e) => setEditCurrIsActive(e.target.checked)}
                                className="w-4 h-4 accent-teal-700"
                              />
                              <span className="text-gray-700 font-semibold">Active</span>
                            </label>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveCurrencyEdit(curr.id)}
                              disabled={savingCurrId === curr.id}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-teal-800 hover:bg-teal-900 text-white rounded-lg text-xs font-semibold transition disabled:opacity-50 cursor-pointer"
                            >
                              <Check className="w-3.5 h-3.5" />
                              {savingCurrId === curr.id ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditCurrency}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-xs hover:bg-gray-50 cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Currency Modal */}
        {showAddCurrency && (
          <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl">
            <h3 className="text-xs font-bold text-teal-900 uppercase tracking-wider mb-2">
              Add New Currency
            </h3>
            <form onSubmit={handleCreateCurrency} className="grid grid-cols-1 sm:grid-cols-5 gap-3 text-xs">
              <div>
                <label className="block text-gray-600 mb-1">Currency Code (3-4 chars) *</label>
                <input
                  type="text"
                  maxLength={6}
                  value={newCurrCode}
                  onChange={(e) => setNewCurrCode(e.target.value.toUpperCase())}
                  placeholder="e.g. ZAR, AED, CAD"
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono font-bold"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Currency Name *</label>
                <input
                  type="text"
                  value={newCurrName}
                  onChange={(e) => setNewCurrName(e.target.value)}
                  placeholder="e.g. South African Rand"
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Symbol *</label>
                <input
                  type="text"
                  value={newCurrSymbol}
                  onChange={(e) => setNewCurrSymbol(e.target.value)}
                  placeholder="e.g. R, د.إ, C$"
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Rate to 1 USD *</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  value={newCurrRate}
                  onChange={(e) => setNewCurrRate(parseFloat(e.target.value) || 1.0)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
                  required
                />
              </div>
              <div className="flex items-end space-x-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-800 text-white rounded-lg font-semibold hover:bg-teal-900 transition"
                >
                  Save Currency
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddCurrency(false)}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* SECTION 2: COMPANY LEGAL HEADER PROFILE */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
        <h2 className="text-base font-bold text-teal-900 flex items-center">
          <Building className="w-5 h-5 mr-2 text-teal-700" /> Company Profile & Legal Registration
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Trading Name *</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-bold"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Legal Company Name *</label>
            <input
              type="text"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-semibold"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Tagline</label>
            <input
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs italic"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-gray-700 font-semibold mb-1">Physical Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Telephone / Hotline</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Official WhatsApp</label>
            <input
              type="text"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Billing / Reservations Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Website URL</label>
            <input
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Tax Identification Number (TIN)</label>
            <input
              type="text"
              value={tin}
              onChange={(e) => setTin(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Company Registration Number</label>
            <input
              type="text"
              value={regNo}
              onChange={(e) => setRegNo(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">VAT Registration Number</label>
            <input
              type="text"
              value={vat}
              onChange={(e) => setVat(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
            />
          </div>
        </div>
      </div>

      {/* SECTION 3: BANK WIRE ACCOUNTS */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-teal-900 flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-teal-700" /> Bank Wire Transfer Accounts
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              These details appear on invoices for clients settling payments via wire transfer
            </p>
          </div>
          <button
            type="button"
            onClick={addBankAccount}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-50 hover:bg-teal-100 text-teal-900 border border-teal-200 text-xs font-semibold rounded-lg shadow-2xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Add Bank Account
          </button>
        </div>

        <div className="space-y-3">
          {bankAccounts.map((b, idx) => (
            <div key={idx} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3 relative">
              <button
                type="button"
                onClick={() => setBankAccounts(bankAccounts.filter((_, i) => i !== idx))}
                className="absolute top-3 right-3 text-gray-400 hover:text-red-600 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Bank Name *</label>
                  <input
                    type="text"
                    value={b.bank_name}
                    onChange={(e) => {
                      const updated = [...bankAccounts];
                      updated[idx].bank_name = e.target.value;
                      setBankAccounts(updated);
                    }}
                    placeholder="e.g. Standard Chartered Bank"
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Account Name *</label>
                  <input
                    type="text"
                    value={b.account_name}
                    onChange={(e) => {
                      const updated = [...bankAccounts];
                      updated[idx].account_name = e.target.value;
                      setBankAccounts(updated);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Account Number / IBAN *</label>
                  <input
                    type="text"
                    value={b.account_number}
                    onChange={(e) => {
                      const updated = [...bankAccounts];
                      updated[idx].account_number = e.target.value;
                      setBankAccounts(updated);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Branch</label>
                  <input
                    type="text"
                    value={b.branch || ''}
                    onChange={(e) => {
                      const updated = [...bankAccounts];
                      updated[idx].branch = e.target.value;
                      setBankAccounts(updated);
                    }}
                    placeholder="e.g. Speke Road Branch, Kampala"
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">SWIFT / BIC Code</label>
                  <input
                    type="text"
                    value={b.swift_bic || ''}
                    onChange={(e) => {
                      const updated = [...bankAccounts];
                      updated[idx].swift_bic = e.target.value;
                      setBankAccounts(updated);
                    }}
                    placeholder="e.g. SCBLUGKX"
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Account Currency</label>
                  <select
                    value={b.currency}
                    onChange={(e) => {
                      const updated = [...bankAccounts];
                      updated[idx].currency = e.target.value;
                      setBankAccounts(updated);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white"
                  >
                    {currencies.map((c) => (
                      <option key={c.id} value={c.code}>
                        {c.code} ({c.name})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
          {bankAccounts.length === 0 && (
            <p className="text-xs text-gray-400 italic py-2">No bank accounts configured yet.</p>
          )}
        </div>
      </div>

      {/* SECTION 4: MOBILE MONEY ACCOUNTS */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-teal-900 flex items-center">
              <Smartphone className="w-5 h-5 mr-2 text-teal-700" /> Mobile Money Merchant Details
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Network codes and merchant pay numbers (MTN MoMo, Airtel Money, M-Pesa)
            </p>
          </div>
          <button
            type="button"
            onClick={addMomoAccount}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-50 hover:bg-teal-100 text-teal-900 border border-teal-200 text-xs font-semibold rounded-lg shadow-2xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Add Mobile Money
          </button>
        </div>

        <div className="space-y-3">
          {momoAccounts.map((m, idx) => (
            <div key={idx} className="p-4 bg-gray-50 border border-gray-200 rounded-xl grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs relative">
              <button
                type="button"
                onClick={() => setMomoAccounts(momoAccounts.filter((_, i) => i !== idx))}
                className="absolute top-3 right-3 text-gray-400 hover:text-red-600 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div>
                <label className="block text-gray-600 font-medium mb-1">Network *</label>
                <input
                  type="text"
                  value={m.network}
                  onChange={(e) => {
                    const updated = [...momoAccounts];
                    updated[idx].network = e.target.value;
                    setMomoAccounts(updated);
                  }}
                  placeholder="e.g. MTN Mobile Money"
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs font-bold"
                />
              </div>
              <div>
                <label className="block text-gray-600 font-medium mb-1">Merchant / Pay Number *</label>
                <input
                  type="text"
                  value={m.merchant_number}
                  onChange={(e) => {
                    const updated = [...momoAccounts];
                    updated[idx].merchant_number = e.target.value;
                    setMomoAccounts(updated);
                  }}
                  placeholder="e.g. 654321"
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-gray-600 font-medium mb-1">Account / Merchant Name *</label>
                <input
                  type="text"
                  value={m.account_name}
                  onChange={(e) => {
                    const updated = [...momoAccounts];
                    updated[idx].account_name = e.target.value;
                    setMomoAccounts(updated);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs font-semibold"
                />
              </div>
              <div>
                <label className="block text-gray-600 font-medium mb-1">Currency</label>
                <input
                  type="text"
                  value={m.currency || 'UGX'}
                  onChange={(e) => {
                    const updated = [...momoAccounts];
                    updated[idx].currency = e.target.value;
                    setMomoAccounts(updated);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs font-bold"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 5: DEFAULT INVOICE & VOUCHER TEMPLATES */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
        <h2 className="text-base font-bold text-teal-900 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-teal-700" /> Standard Notes & Terms Templates
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Default Invoice Notes</label>
            <textarea
              rows={3}
              value={invoiceNotes}
              onChange={(e) => setInvoiceNotes(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Default Invoice Terms & Conditions</label>
            <textarea
              rows={3}
              value={invoiceTerms}
              onChange={(e) => setInvoiceTerms(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Voucher Supplier Instructions</label>
            <textarea
              rows={3}
              value={voucherSupplierInst}
              onChange={(e) => setVoucherSupplierInst(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Voucher Client Instructions</label>
            <textarea
              rows={3}
              value={voucherClientInst}
              onChange={(e) => setVoucherClientInst(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-xs"
            />
          </div>
        </div>
      </div>

      {/* SECTION 6: INVOICE & SUPPLIER CATEGORIES */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-6">
        <div>
          <h2 className="text-base font-bold text-teal-900 flex items-center">
            <Tag className="w-5 h-5 mr-2 text-teal-700" /> Invoice Line Item & Supplier Categories
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Configure line item categories for invoicing clients and business categories for registering suppliers and partners.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Column 1: Invoice Line Item Categories */}
          <div className="bg-gray-50/60 rounded-xl p-4.5 border border-gray-200/80 space-y-3.5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Invoice Line Item Categories</h3>
                <p className="text-[11px] text-gray-500">Used when adding services to invoices & costing</p>
              </div>
              <button
                type="button"
                onClick={handleResetInvoiceCategories}
                className="text-[11px] text-gray-500 hover:text-teal-700 flex items-center gap-1 font-medium transition cursor-pointer"
              >
                <RotateCcw className="w-3 h-3" /> Reset Defaults
              </button>
            </div>

            {/* Quick Add Form */}
            <form onSubmit={handleAddInvoiceCategory} className="flex gap-2">
              <input
                type="text"
                value={newInvoiceCategoryLabel}
                onChange={(e) => setNewInvoiceCategoryLabel(e.target.value)}
                placeholder="e.g. Visa Fees, Boat Safaris, Insurance..."
                className="flex-1 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
              <button
                type="submit"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-800 hover:bg-teal-900 text-white rounded-lg text-xs font-semibold shadow-2xs transition cursor-pointer shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </form>

            {/* List */}
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {invoiceCategories.map((cat) => {
                const isEditing = editingInvoiceCatId === cat.id;
                return (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-200 text-xs shadow-2xs"
                  >
                    {isEditing ? (
                      <div className="flex items-center gap-2 flex-1 mr-2">
                        <input
                          type="text"
                          value={editingInvoiceCatLabel}
                          onChange={(e) => setEditingInvoiceCatLabel(e.target.value)}
                          className="flex-1 px-2 py-0.5 text-xs border border-teal-500 rounded focus:outline-none bg-white font-medium"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEditInvoiceCategory(cat.id);
                            if (e.key === 'Escape') setEditingInvoiceCatId(null);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleSaveEditInvoiceCategory(cat.id)}
                          className="p-1 text-teal-700 hover:bg-teal-50 rounded"
                          title="Save"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingInvoiceCatId(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          title="Cancel"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                          <span className="font-semibold text-gray-900 truncate">{cat.label}</span>
                          <span className="font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
                            {cat.id}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingInvoiceCatId(cat.id);
                              setEditingInvoiceCatLabel(cat.label);
                            }}
                            className="p-1 text-gray-400 hover:text-teal-700 hover:bg-gray-50 rounded transition"
                            title="Edit name"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteInvoiceCategory(cat.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Column 2: Supplier / Partner Categories */}
          <div className="bg-gray-50/60 rounded-xl p-4.5 border border-gray-200/80 space-y-3.5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Supplier / Partner Categories</h3>
                <p className="text-[11px] text-gray-500">Used when adding safari lodges, operators & vendors</p>
              </div>
              <button
                type="button"
                onClick={handleResetSupplierCategories}
                className="text-[11px] text-gray-500 hover:text-teal-700 flex items-center gap-1 font-medium transition cursor-pointer"
              >
                <RotateCcw className="w-3 h-3" /> Reset Defaults
              </button>
            </div>

            {/* Quick Add Form */}
            <form onSubmit={handleAddSupplierCategory} className="flex gap-2">
              <input
                type="text"
                value={newSupplierCategoryLabel}
                onChange={(e) => setNewSupplierCategoryLabel(e.target.value)}
                placeholder="e.g. Balloon Safari, Boat Operator, Cultural..."
                className="flex-1 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
              <button
                type="submit"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-800 hover:bg-teal-900 text-white rounded-lg text-xs font-semibold shadow-2xs transition cursor-pointer shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </form>

            {/* List */}
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {supplierCategories.map((cat) => {
                const isEditing = editingSupplierCatId === cat.id;
                return (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-200 text-xs shadow-2xs"
                  >
                    {isEditing ? (
                      <div className="flex items-center gap-2 flex-1 mr-2">
                        <input
                          type="text"
                          value={editingSupplierCatLabel}
                          onChange={(e) => setEditingSupplierCatLabel(e.target.value)}
                          className="flex-1 px-2 py-0.5 text-xs border border-teal-500 rounded focus:outline-none bg-white font-medium"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEditSupplierCategory(cat.id);
                            if (e.key === 'Escape') setEditingSupplierCatId(null);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleSaveEditSupplierCategory(cat.id)}
                          className="p-1 text-teal-700 hover:bg-teal-50 rounded"
                          title="Save"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingSupplierCatId(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          title="Cancel"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                          <span className="font-semibold text-gray-900 truncate">{cat.label}</span>
                          <span className="font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
                            {cat.id}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingSupplierCatId(cat.id);
                              setEditingSupplierCatLabel(cat.label);
                            }}
                            className="p-1 text-gray-400 hover:text-teal-700 hover:bg-gray-50 rounded transition"
                            title="Edit name"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSupplierCategory(cat.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Save Action Bar */}
      <div className="flex justify-end items-center pt-2 pb-12">
        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={saving}
          className="inline-flex items-center gap-2 px-7 py-3 bg-teal-800 hover:bg-teal-900 text-white rounded-xl text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-150 disabled:opacity-50 cursor-pointer"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};
